import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
// Stesso stile campi gym (dark) di AddMember / filtri membri
const fieldClassName =
    'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder:text-white/50 dark:focus:border-indigo-500 dark:focus:ring-white/30';

const labelClassName = 'mb-1 block text-sm font-medium text-gray-700 dark:text-white/85';

function formatListPrice(price) {
    const n = Number(price);
    if (Number.isNaN(n)) return '—';
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

// Modal per creare nuovi abbonamenti
export default function CreateSubscriptionModal({ client, isOpen, onClose, onSuccess, mode = 'create', initialSubscription = null }) {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();
    const isEditMode = mode === 'edit' && initialSubscription;

    const buildDefaultFormData = useCallback(() => {
        const startDate = new Date();
        const endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + 1);

        return {
            subscription_name: '',
            subscription_type: 'monthly',
            price: '',
            payment_method: 'cash',
            start_date: startDate.toISOString().split('T')[0],
            end_date: endDate.toISOString().split('T')[0],
            notes: ''
        };
    }, []);

    const [formData, setFormData] = useState(() => buildDefaultFormData());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [priceList, setPriceList] = useState([]);
    const [priceListLoading, setPriceListLoading] = useState(false);
    /** '' = personalizzato; altrimenti id voce listino */
    const [selectedPriceListId, setSelectedPriceListId] = useState('');

    const calculateEndDate = useCallback(() => {
        if (!formData.start_date) return;
        
        const startDate = new Date(formData.start_date);
        const endDate = new Date(startDate);
        
        switch (formData.subscription_type) {
            case 'monthly':
                endDate.setMonth(endDate.getMonth() + 1);
                break;
            case 'quarterly':
                endDate.setMonth(endDate.getMonth() + 3);
                break;
            case 'yearly':
                endDate.setFullYear(endDate.getFullYear() + 1);
                break;
            default:
                endDate.setMonth(endDate.getMonth() + 1);
        }
        
        setFormData(prev => ({
            ...prev,
            end_date: endDate.toISOString().split('T')[0]
        }));
    }, [formData.start_date, formData.subscription_type]);

    useEffect(() => {
        if (isOpen && mode === 'create') {
            calculateEndDate();
        }
    }, [isOpen, mode, formData.subscription_type, formData.start_date, calculateEndDate]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        if (isEditMode && initialSubscription) {
            const method = initialSubscription.payment_method;
            setFormData({
                subscription_name: initialSubscription.subscription_name || '',
                subscription_type: initialSubscription.subscription_type || 'monthly',
                price: initialSubscription.price && Number(initialSubscription.price) > 0 ? String(initialSubscription.price) : '',
                payment_method: ['cash', 'card', 'other'].includes(method) ? method : 'cash',
                start_date: initialSubscription.start_date ? initialSubscription.start_date.slice(0, 10) : buildDefaultFormData().start_date,
                end_date: initialSubscription.end_date ? initialSubscription.end_date.slice(0, 10) : buildDefaultFormData().end_date,
                notes: initialSubscription.notes || ''
            });
        } else {
            setFormData(buildDefaultFormData());
        }
        setSelectedPriceListId('');
        setError(null);
    }, [isOpen, isEditMode, initialSubscription, buildDefaultFormData]);

    useEffect(() => {
        if (!isOpen) return;

        let cancelled = false;
        (async () => {
            try {
                setPriceListLoading(true);
                const api = getAuthenticatedApi();
                const res = await api.get('/gym_subscription_price_list.php?action=list');
                if (cancelled) return;
                if (res.data?.success && Array.isArray(res.data.items)) {
                    setPriceList(res.data.items);
                } else {
                    setPriceList([]);
                }
            } catch (err) {
                if (!cancelled) {
                    // eslint-disable-next-line no-console
                    console.error('Errore caricamento listino:', err);
                    setPriceList([]);
                }
            } finally {
                if (!cancelled) setPriceListLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isOpen, getAuthenticatedApi]);

    const applyPriceListItem = (itemId) => {
        setSelectedPriceListId(itemId);
        if (!itemId) {
            return;
        }
        const item = priceList.find((row) => String(row.id) === String(itemId));
        if (!item) return;
        setFormData((prev) => ({
            ...prev,
            subscription_name: item.name || '',
            subscription_type: item.subscription_type || 'monthly',
            price: item.price != null ? String(item.price) : '',
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            if (formData.start_date && formData.end_date && formData.start_date > formData.end_date) {
                setError(t('gym.subscriptionModal.errors.endBeforeStart', 'La data di fine non può precedere la data di inizio'));
                setLoading(false);
                return;
            }

            const api = getAuthenticatedApi();
            if (!formData.payment_method) {
                setError(t('gym.subscriptionModal.errors.paymentMethodRequired'));
                setLoading(false);
                return;
            }

            const payload = {
                client_id: client.id,
                subscription_name: formData.subscription_name,
                subscription_type: formData.subscription_type,
                price: formData.price === '' ? 0 : parseFloat(formData.price),
                payment_method: formData.payment_method,
                start_date: formData.start_date,
                end_date: formData.end_date,
                notes: formData.notes || ''
            };

            if (isEditMode && initialSubscription?.id) {
                payload.subscription_id = initialSubscription.id;
            }

            const endpoint = isEditMode
                ? '/client_subscription_management.php?action=update_subscription'
                : '/client_subscription_management.php?action=create_subscription';

            const response = await api.post(endpoint, payload);

            if (response.data.success) {
                onSuccess();
            } else {
                setError(response.data.error || t(isEditMode ? 'gym.subscriptionModal.errors.updateError' : 'gym.subscriptionModal.errors.createError'));
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Errore creazione abbonamento:', err);
            setError(t('gym.subscriptionModal.errors.connectionError'));
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (['subscription_name', 'subscription_type', 'price'].includes(name) && selectedPriceListId) {
            setSelectedPriceListId('');
        }
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    if (!isOpen) return null;

    return (
            <div className="w-full p-6">
                {/* Header rimosso - IOSModal lo gestisce */}

                {error && (
                    <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded-lg">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className={labelClassName}>
                            {t('gym.subscriptionModal.form.priceList')}
                        </label>
                        <select
                            value={selectedPriceListId}
                            onChange={(e) => applyPriceListItem(e.target.value)}
                            className={fieldClassName}
                            disabled={priceListLoading}
                        >
                            <option value="">
                                {priceListLoading
                                    ? t('gym.subscriptionModal.form.priceListLoading')
                                    : t('gym.subscriptionModal.form.priceListCustom')}
                            </option>
                            {priceList.map((item) => (
                                <option key={item.id} value={item.id}>
                                    {item.name} — €{formatListPrice(item.price)} ({t(`gym.subscriptionModal.subscriptionTypes.${item.subscription_type}`, item.subscription_type)})
                                </option>
                            ))}
                        </select>
                        <p className="mt-1 text-xs text-gray-500 dark:text-white/55">
                            {priceList.length === 0 && !priceListLoading
                                ? t('gym.subscriptionModal.form.priceListEmptyHint')
                                : t('gym.subscriptionModal.form.priceListHint')}
                        </p>
                    </div>

                    <div>
                        <label className={labelClassName}>
                            {t('gym.subscriptionModal.form.subscriptionName')}
                        </label>
                        <input
                            type="text"
                            name="subscription_name"
                            value={formData.subscription_name}
                            onChange={handleInputChange}
                            className={fieldClassName}
                            placeholder={t('gym.subscriptionModal.form.subscriptionNamePlaceholder')}
                            required
                        />
                    </div>

                    <div>
                        <label className={labelClassName}>
                            {t('gym.subscriptionModal.form.subscriptionType')}
                        </label>
                        <select
                            name="subscription_type"
                            value={formData.subscription_type}
                            onChange={handleInputChange}
                            className={fieldClassName}
                        >
                            <option value="monthly">{t('gym.subscriptionModal.subscriptionTypes.monthly')}</option>
                            <option value="quarterly">{t('gym.subscriptionModal.subscriptionTypes.quarterly')}</option>
                            <option value="yearly">{t('gym.subscriptionModal.subscriptionTypes.yearly')}</option>
                            <option value="custom">{t('gym.subscriptionModal.subscriptionTypes.custom')}</option>
                        </select>
                    </div>

                    <div>
                        <label className={labelClassName}>
                            {t('gym.subscriptionModal.form.price')}
                        </label>
                        <input
                            type="number"
                            name="price"
                            value={formData.price}
                            onChange={handleInputChange}
                            className={fieldClassName}
                            placeholder={t('gym.subscriptionModal.form.pricePlaceholder')}
                            step="0.01"
                            min="0"
                        />
                    </div>

                    <div>
                        <label className={labelClassName}>
                            {t('gym.subscriptionModal.form.paymentMethod')}
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                            {[
                                { id: 'cash', label: t('gym.subscriptionModal.paymentMethods.cash') },
                                { id: 'card', label: t('gym.subscriptionModal.paymentMethods.card') },
                                { id: 'other', label: t('gym.subscriptionModal.paymentMethods.other') },
                            ].map((opt) => {
                                const active = formData.payment_method === opt.id;
                                return (
                                    <button
                                        key={opt.id}
                                        type="button"
                                        onClick={() =>
                                            setFormData((prev) => ({ ...prev, payment_method: opt.id }))
                                        }
                                        className={`rounded-md px-2 py-2 text-sm font-medium transition-colors border ${
                                            active
                                                ? 'border-indigo-600 bg-indigo-600 text-white'
                                                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-white/85 dark:hover:bg-gray-700'
                                        }`}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={labelClassName}>
                                {t('gym.subscriptionModal.form.startDate')}
                            </label>
                            <input
                                type="date"
                                name="start_date"
                                value={formData.start_date}
                                onChange={handleInputChange}
                                className={fieldClassName}
                                required
                            />
                        </div>
                        <div>
                            <label className={labelClassName}>
                                {t('gym.subscriptionModal.form.endDate')}
                            </label>
                            <input
                                type="date"
                                name="end_date"
                                value={formData.end_date}
                                onChange={handleInputChange}
                                className={fieldClassName}
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className={labelClassName}>
                            {t('gym.subscriptionModal.form.notes')}
                        </label>
                        <textarea
                            name="notes"
                            value={formData.notes}
                            onChange={handleInputChange}
                            className={`${fieldClassName} resize-y`}
                            rows="3"
                            placeholder={t('gym.subscriptionModal.form.notesPlaceholder')}
                        />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-md bg-gray-200 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-300 dark:bg-gray-800 dark:text-white/90 dark:hover:brightness-110"
                        >
                            {t('gym.subscriptionModal.buttons.cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="rounded-md bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 disabled:opacity-50 dark:bg-indigo-600 dark:text-white dark:hover:brightness-110"
                        >
                            {loading
                                ? t(isEditMode ? 'gym.subscriptionModal.buttons.saving' : 'gym.subscriptionModal.buttons.creating')
                                : t(isEditMode ? 'gym.subscriptionModal.buttons.save' : 'gym.subscriptionModal.buttons.create')}
                        </button>
                    </div>
                </form>
            </div>
    );
}
